import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { type FC, type ReactNode, useState } from "react";

import CustomAlert from "../alerts/CustomAlert";
import FormLoader from "../skeleton/FormSkeleton";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils/core/generalUtils";

type FormDialogCloseProps =
  | { onClose: () => void; toggleModal?: never }
  | { toggleModal: () => void; onClose?: never };

type FormDialogProps = {
  isFormDirty?: boolean;
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  trigger?: ReactNode;
  isLoading?: boolean;
} & FormDialogCloseProps &
  (
    | { title?: string; subtitle?: string; header?: never }
    | { title?: never; subtitle?: string; header?: ReactNode }
  );

const FormDialog: FC<FormDialogProps> = ({
  isFormDirty,
  isOpen,
  onClose,
  toggleModal,
  trigger,
  children,
  className,
  isLoading,
  ...props
}) => {
  const [warning, setWarning] = useState(false);
  const closeDialog = onClose ?? toggleModal;

  const handleModalOpenChange = (nextOpen: boolean): void => {
    if (nextOpen || !isOpen) {
      return;
    }

    if (isFormDirty) {
      setWarning(true);
    } else {
      closeDialog();
    }
  };

  const renderChildren = (): ReactNode => {
    if (isLoading) {
      return <FormLoader />;
    }

    if (props.title) {
      return (
        <div>
          <DialogHeader className='space-y-4'>
            <DialogTitle className='text-2xl font-normal text-center Capitalize'>
              {props.title} <br />
              <p className='my-1 text-sm text-center opacity-40'>
                {props?.subtitle || ""}
              </p>
            </DialogTitle>
          </DialogHeader>
          {children}
        </div>
      );
    }

    return (
      <>
        {props.header}
        {children}
      </>
    );
  };

  return (
    <div className='relative'>
      <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
        <VisuallyHidden>
          <DialogTitle />
          <DialogDescription />
        </VisuallyHidden>
        {trigger}
        <DialogContent
          className={cn(
            "h-fit p-0 max-w-screen-2xl md:max-w-2xl flex flex-col justify-between",
            className,
          )}
        >
          {renderChildren()}
        </DialogContent>
      </Dialog>

      <CustomAlert
        isOpen={warning}
        setIsOpen={setWarning}
        btnAction={closeDialog}
        title='Are you sure?'
        desc='If you proceed, this action cannot be undone. Are you sure you want to continue? Form data will be lost.'
        cancelName='No, Continue Editing'
        btnName='Yes, Cancel'
      />
    </div>
  );
};

export default FormDialog;
